// =============================================================================
// useInstructorSchedule — SQL RPC-based slot hooks (ADR-0001)
// =============================================================================
//
// These hooks are the single authoritative source for slot availability on the
// client. They call calculate_instructor_available_slots() via supabase.rpc()
// and cache results with React Query (staleTime: 5 min).
//
// Do NOT replicate slot-calculation logic in JS — see ADR-0001 for why.
// =============================================================================

import { useQuery, useQueries } from '@tanstack/react-query';
import { format, addDays, startOfDay } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AvailableSlot {
  start_time:       string;  // "HH:MM:SS" — matches DB TIME column
  end_time:         string;  // "HH:MM:SS"
  duration_minutes: number;
  is_available:     boolean;
}

export interface DaySlotCount {
  date:      string; // "YYYY-MM-DD"
  slotCount: number;
  isLoading: boolean;
}

// ── useInstructorSchedule ─────────────────────────────────────────────────────

/**
 * Returns available slots for an instructor on a specific date.
 *
 * Calls calculate_instructor_available_slots(p_instructor_id, p_target_date).
 * Returns empty array when agenda is private, unconfigured, or date is past.
 * Slots already exclude trainings and approved class requests (server-side).
 */
export const useInstructorSchedule = (
  instructorId: string | undefined,
  date: Date,
) => {
  const dateStr = format(date, 'yyyy-MM-dd');

  const { data: slots = [], isLoading } = useQuery<AvailableSlot[]>({
    queryKey: ['instructor-slots', instructorId, dateStr],
    queryFn: async (): Promise<AvailableSlot[]> => {
      if (!instructorId) return [];

      const { data, error } = await supabase.rpc(
        'calculate_instructor_available_slots',
        { p_instructor_id: instructorId, p_target_date: dateStr },
      );

      if (error) throw error;
      return (data as AvailableSlot[]) ?? [];
    },
    enabled: !!instructorId,
    staleTime: 5 * 60 * 1000, // 5 minutes — schedule rarely changes mid-session
  });

  return { slots, isLoading };
};

// ── useInstructorScheduleCounts ───────────────────────────────────────────────

/**
 * Returns per-day slot counts for the next `daysAhead` days.
 *
 * Uses the same query keys as useInstructorSchedule, so results are served
 * from cache when the user navigates to a day that was already pre-fetched.
 *
 * Each query is independent — React Query fires them in parallel and caches
 * each day's result separately for 5 minutes.
 */
export const useInstructorScheduleCounts = (
  instructorId: string | undefined,
  daysAhead: number = 14,
): DaySlotCount[] => {
  const today = startOfDay(new Date());
  const dateStrings = Array.from({ length: daysAhead }, (_, i) =>
    format(addDays(today, i), 'yyyy-MM-dd'),
  );

  const results = useQueries({
    queries: dateStrings.map(dateStr => ({
      queryKey: ['instructor-slots', instructorId, dateStr] as const,
      queryFn: async (): Promise<AvailableSlot[]> => {
        if (!instructorId) return [];

        const { data, error } = await supabase.rpc(
          'calculate_instructor_available_slots',
          { p_instructor_id: instructorId, p_target_date: dateStr },
        );

        if (error) return [];
        return (data as AvailableSlot[]) ?? [];
      },
      enabled: !!instructorId,
      staleTime: 5 * 60 * 1000,
    })),
  });

  return dateStrings.map((dateStr, i) => ({
    date:      dateStr,
    slotCount: results[i].data?.length ?? 0,
    isLoading: results[i].isLoading,
  }));
};
